import React, { Context, ReactNode, useCallback, useRef } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import S3 from 'aws-sdk/clients/s3';
import { ExecutionResult } from 'graphql';

import { queryStrings } from '@trqk/resolvers';
import {
    generateAudioFileKeyForS3,
    getWebpackEnvVar,
    removeNulls,
} from '@trqk/utils';

import { useTokenModal, useTasks, useToast, useDetectionTracks } from '..';
import { checkAudioFiles } from '../../utils';

const { GET_SESSION_CURRENT_USER, TASK_UPLOAD_AUDIO_FILE_TO_TRACK } =
    queryStrings;

export const GET_TRQK_SONG = gql`
    query getTrqkSong($id: ID!) {
        getTrqkSong(id: $id) {
            id
            filename
            asset_url
        }
    }
`;

interface UploadTracksProviderProps {
    children: ReactNode;
    UploadTracksContext: Context<UploadTracksProviderValue>;
}

/**
 * A provider that creates the context for the useUploadTracks hook, which allows the user to upload
 * an audio file to an existing work.
 * @param props - {@link UploadTracksProviderProps}
 * @returns JSX
 */
export const UploadTracksProvider = ({
    children,
    UploadTracksContext,
}: UploadTracksProviderProps): JSX.Element => {
    const bucketName = getWebpackEnvVar(
        'web',
        'AWS_S3_USER_AUDIO_FILES_BUCKET',
    );
    const bucketRegion = getWebpackEnvVar('web', 'AWS_REGION');
    const identityPoolId = getWebpackEnvVar('web', 'AWS_IDENTITY_POOL_ID');
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const { sendTaskEvent } = useTasks();
    const { add: addToast } = useToast();
    const { getToken } = useTokenModal();
    const { setDetectionTracks } = useDetectionTracks();
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;

    const [taskUploadAudioFileToTrack] = useMutation(
        TASK_UPLOAD_AUDIO_FILE_TO_TRACK,
        {
            onError: () => {
                // TODO: relay errors to API endpoint
                // https://app.asana.com/0/1161035310203426/1180019971791221
            },
            /**
             * Update the trqkSong in the Apollo Client cache with the new asset_url and filename
             */
            update: (
                cache,
                result: ExecutionResult<{
                    taskUploadAudioFileToTrack: UploadAudioFileToTrackOutput;
                }>,
            ) => {
                const results =
                    result?.data?.taskUploadAudioFileToTrack ?? null;

                if (results !== null) {
                    const {
                        trqkAudioId,
                        trqkSongId,
                        uploadSuccess,
                        createTrqkAudioSuccess,
                    } = results;

                    if (
                        trqkAudioId &&
                        trqkSongId &&
                        uploadSuccess &&
                        createTrqkAudioSuccess
                    ) {
                        try {
                            const cacheQuery = cache.readQuery({
                                query: GET_TRQK_SONG,
                                variables: {
                                    id: trqkSongId,
                                },
                            });

                            const currentTrqkSong =
                                // eslint-disable-next-line
                                // @ts-ignore
                                (cacheQuery?.getTrqkSong as TrqkSong) ?? null;

                            if (currentTrqkSong !== null) {
                                cache.writeQuery({
                                    query: GET_TRQK_SONG,
                                    variables: { id: trqkSongId },
                                    data: {
                                        getTrqkSong: {
                                            ...currentTrqkSong,
                                            audio_id: trqkAudioId,
                                        },
                                    },
                                });
                            }
                        } catch {
                            // It's possible that this cache update will be called before the client
                            // has actually queried for this trqkSong id before. In that case, the
                            // cache will throw an error, and we should ignore it.
                        }
                    }
                }
            },
        },
    );

    // Each upload results in a ManagedUpload instance that we will need access to in order to
    // allow the user to cancel the upload. We use a ref to maintain access to a single object
    // that contains each of the ManagedUpload instances.
    const managedUploads = useRef({} as ManagedUploadsRef);
    // onTaskCancel is bound to the 'CANCEL' event when the task is started. This allows us to
    // call: `sendTaskEvent({ type: 'CANCEL', value: { taskId }})` within <ManageStatementsUploads>
    // in order to:
    //  1) notify the task machine to cancel the task, and:
    //  2) cancel the upload with managedUpload.abort()
    const onTaskCancel = (task: UploadTrackTask): void => {
        const managedUpload = managedUploads?.current?.[task.taskId];

        if (managedUpload) {
            managedUpload.abort();
            delete managedUploads.current[task.taskId];

            if (!task.error) {
                addToast({
                    content: `Track upload canceled for: ${task?.input?.title}`,
                    kind: 'warning-banner',
                });
            }
        }
    };

    const handleFileForTrack = useCallback(
        ({
            file,
            percentShares,
            taskId,
            title,
            trqkSongId,
            onTaskUpdate,
            onSuccess,
            workIds,
        }: HandleFileForTrackInput): void => {
            const groupId = `use-upload-track-task-group-${trqkSongId}`;

            const validFiles = checkAudioFiles({ addToast, files: [file] });

            if (validFiles === null) {
                return undefined;
            }

            const taskCreators = [
                {
                    taskId,
                    input: {
                        filename: file.name,
                        title,
                    },
                    onTaskStart: (bindOnTaskCancel: BindOnTaskCancel): void => {
                        bindOnTaskCancel(onTaskCancel);

                        taskUploadAudioFileToTrack({
                            context: {
                                bindManagedUpload: (
                                    managedUpload: S3.ManagedUpload,
                                ): void => {
                                    if (managedUploads?.current) {
                                        managedUploads.current[taskId] =
                                            managedUpload;
                                    }
                                },
                                getToken,
                                generateS3Key: (currentFile: File): string =>
                                    generateAudioFileKeyForS3({
                                        file: currentFile,
                                        userId,
                                    }),
                                sendTaskEvent,
                            },
                            variables: {
                                bucketName,
                                bucketRegion,
                                file,
                                identityPoolId,
                                taskId,
                                trqkDirectoryUrl,
                                trqkSongId,
                            },
                        });
                    },
                },
            ];

            sendTaskEvent({
                type: 'CREATE_GROUP',
                value: {
                    groupId,
                    taskCreators,
                    onTaskUpdate: (task: UploadTrackTask): void => {
                        if (onTaskUpdate) {
                            onTaskUpdate(task);
                        }

                        if (task.error && !task.canceled) {
                            addToast({
                                kind: 'warning-banner',
                                content: `Track upload failed for: ${task?.input?.title}`,
                            });
                        }

                        if (task.success) {
                            addToast({
                                kind: 'info-banner',
                                content: `Track upload succeeded for: ${task?.input?.title}`,
                            });

                            if (onSuccess) {
                                onSuccess(task);
                            }

                            // Upon successful creation of a trqkAudio, update the global
                            // detectionTracks list with the new data

                            const newTrqkAudio: TrqkAudio | null =
                                task?.output?.trqkAudio ?? null;

                            if (newTrqkAudio !== null) {
                                const { id } = newTrqkAudio;

                                setDetectionTracks(
                                    (
                                        prev: DetectionTrack[],
                                    ): DetectionTrack[] => [
                                        ...prev,
                                        {
                                            ...newTrqkAudio,
                                            detection_history:
                                                removeNulls<DetectionStatusChange>(
                                                    newTrqkAudio.detection_history,
                                                ),
                                            audio_id: id,
                                            title,
                                            work_ids: workIds,
                                            percent_shares: percentShares,
                                        },
                                    ],
                                );
                            }
                        }
                    },
                },
            });

            return undefined;
        },
        [sendTaskEvent, addToast, managedUploads.current, userId],
    );

    const cancel = useCallback(
        (currentTaskId: string): void => {
            sendTaskEvent({
                type: 'CANCEL',
                value: {
                    taskId: currentTaskId,
                },
            });
        },
        [sendTaskEvent],
    );

    return (
        <UploadTracksContext.Provider
            value={{
                cancel,
                handleFileForTrack,
            }}
        >
            {children}
        </UploadTracksContext.Provider>
    );
};
